import { getInternalAppSdk } from '@/utils/internalAppSdk'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import type { IntentGetQuoteResponse } from '@vaultsfyi/common/schemas'
import type { Network } from '@vaultsfyi/common/types'
import type { Address } from 'viem'

const crosschainQuoteQueryKey = ['crosschainQuote'] as const

interface CrosschainQuoteArgs {
  userAddress: Address | undefined
  inputToken:
    | {
        address: Address
        network: Network
      }
    | undefined
  outputVault: {
    address: Address
    network: Network
  }
  amount: string
  enabled?: boolean
}

export type { IntentGetQuoteResponse as CrosschainQuoteResponse }

const crosschainQuoteQueryOptions = ({
  userAddress,
  inputToken,
  outputVault,
  amount,
  enabled = true,
}: CrosschainQuoteArgs) =>
  queryOptions({
    queryKey: [
      ...crosschainQuoteQueryKey,
      {
        userAddress,
        inputToken,
        outputVault,
        amount,
      },
    ] as const,
    queryFn(): Promise<IntentGetQuoteResponse> {
      if (!userAddress || !inputToken || !amount) throw new Error('Missing required query parameters')
      const internalAppSdk = getInternalAppSdk()

      return internalAppSdk.getIntentQuote({ userAddress, inputToken, outputVault, amount })
    },
    enabled,
    retry: false,
    staleTime: 30 * SECOND_IN_MILLISECONDS,
    refetchOnWindowFocus: false,
  })

export const useCrosschainQuoteQuery = (args: CrosschainQuoteArgs) => {
  return useQuery({
    ...crosschainQuoteQueryOptions(args),
  })
}

export type CrosschainQuote = IntentGetQuoteResponse | undefined
